/**
 * SESSION MANAGER HOOK
 * ====================
 * 
 * Handles:
 * - Activity detection (mouse, keyboard, scroll, click)
 * - Auto session extension when user is active
 * - Auto logout when session expires and user is inactive
 * - Warning modal before expiry
 * - Multi-tab synchronization
 * 
 * Usage:
 *   const { showWarning, timeLeft, extendSession, logout } = useSessionManager();
 */

import { useState, useEffect, useRef, useCallback } from "react";
import Cookies from "js-cookie";
import api, { getToken, setToken, clearAuth, getTokenExpiry, isTokenExpired, getTimeUntilExpiry, authApi } from "./Api";

// ============================================
// CONFIGURATION
// ============================================
const CONFIG = {
  // How long user can be inactive before logout (after token expires)
  inactivityTimeout: 5 * 60 * 1000,       // 5 minutes of inactivity = logout
  
  // When to show warning before token expires
  warningBeforeExpiry: 2 * 60 * 1000,     // Show warning 2 min before expiry
  
  // How often to check session status
  checkInterval: 30 * 1000,                // Check every 30 seconds
  
  // Debounce activity detection
  activityDebounce: 1000,                  // 1 second debounce
  
  // Events that count as user activity
  activityEvents: ["mousedown", "keydown", "scroll", "touchstart", "click"],
  
  // LocalStorage keys for cross-tab sync
  keys: {
    lastActivity: "lastActivityTime",
    logout: "sessionLogout",
  },
};

// ============================================
// SESSION MANAGER HOOK
// ============================================
export function useSessionManager(options = {}) {
  const config = { ...CONFIG, ...options };

  // State
  const [showWarning, setShowWarning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);
  const [isActive, setIsActive] = useState(true);

  // Refs
  const lastActivityRef = useRef(Date.now());
  const checkIntervalRef = useRef(null);
  const activityTimeoutRef = useRef(null);
  const isExtendingRef = useRef(false);

  // ----------------------------------------
  // Update last activity time
  // ----------------------------------------
  const updateActivity = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    localStorage.setItem(config.keys.lastActivity, now.toString());
    setIsActive(true);

    // Clear previous timeout
    if (activityTimeoutRef.current) {
      clearTimeout(activityTimeoutRef.current);
    }

    // Set inactive after debounce period
    activityTimeoutRef.current = setTimeout(() => {
      setIsActive(false);
    }, config.inactivityTimeout);
  }, [config.keys.lastActivity, config.inactivityTimeout]);

  // ----------------------------------------
  // Extend session (call backend)
  // ----------------------------------------
  const extendSession = useCallback(async () => {
    if (isExtendingRef.current) return false;
    isExtendingRef.current = true;

    try {
      const token = getToken();
      if (!token) {
        throw new Error("No token");
      }

      const response = await authApi.post("extend_session", {session_token: token});
      // Backend response shape: { status, message, data: { access_token, token_type } }
      const newToken = response.data?.data?.access_token;

      if (newToken && typeof newToken === "string") {
        setToken(newToken);
        setShowWarning(false);
        updateActivity();
        console.log("[Session] Extended successfully");
        return true;
      }

      return false;
    } catch (err) {
      console.error("[Session] Extension failed:", err);
      return false;
    } finally {
      isExtendingRef.current = false;
    }
  }, [updateActivity]);

  // ----------------------------------------
  // Logout
  // ----------------------------------------
  const logout = useCallback(async (reason = "manual") => {
    console.log(`[Session] Logout triggered: ${reason}`);

    const token = getToken();

    // Try to notify server
    if (token) {
      try {
        await api.post("/logout");
      } catch (err) {
        // Ignore - we're logging out anyway
      }
    }

    // Clear all auth data
    clearAuth();

    // Broadcast logout to other tabs
    localStorage.setItem(config.keys.logout, Date.now().toString());

    // Clear intervals
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
    }
    if (activityTimeoutRef.current) {
      clearTimeout(activityTimeoutRef.current);
    }

    // Redirect to login
    window.location.href = "/";
  }, [config.keys.logout]);

  // ----------------------------------------
  // Check session status
  // ----------------------------------------
  const checkSession = useCallback(async () => {
    const token = getToken();

    // No token - logout
    if (!token) {
      logout("no_token");
      return;
    }

    // Check token expiry
    if (isTokenExpired(token)) {
      // Token expired - check if user was active
      const lastActivity = parseInt(localStorage.getItem(config.keys.lastActivity) || "0", 10);
      const timeSinceActivity = Date.now() - lastActivity;

      if (timeSinceActivity < config.inactivityTimeout) {
        // User was active recently - try to extend
        const extended = await extendSession();
        if (!extended) {
          logout("token_expired_extend_failed");
        }
      } else {
        // User inactive - logout
        logout("token_expired_inactive");
      }
      return;
    }

    // Token still valid - check time remaining
    const remaining = getTimeUntilExpiry(token);
    setTimeLeft(remaining);

    // Show warning if close to expiry
    if (remaining > 0 && remaining <= config.warningBeforeExpiry) {
      // Check if user is active
      const lastActivity = parseInt(localStorage.getItem(config.keys.lastActivity) || "0", 10);
      const timeSinceActivity = Date.now() - lastActivity;

      if (timeSinceActivity < config.activityDebounce * 10) {
        // User is active - extend session automatically
        await extendSession();
      } else {
        // User inactive - show warning
        setShowWarning(true);
      }
    } else {
      setShowWarning(false);
    }
  }, [config, extendSession, logout]);

  // ----------------------------------------
  // Handle activity events
  // ----------------------------------------
  useEffect(() => {
    const token = getToken();
    if (!token) return;

    // Throttled activity handler
    let lastHandled = 0;
    const handleActivity = () => {
      const now = Date.now();
      if (now - lastHandled > config.activityDebounce) {
        lastHandled = now;
        updateActivity();
      }
    };

    // Add event listeners
    config.activityEvents.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Initial activity
    updateActivity();

    return () => {
      config.activityEvents.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [config.activityEvents, config.activityDebounce, updateActivity]);

  // ----------------------------------------
  // Session check interval
  // ----------------------------------------
  useEffect(() => {
    const token = getToken();
    if (!token) return;

    // Initial check
    checkSession();

    // Start interval
    checkIntervalRef.current = setInterval(checkSession, config.checkInterval);

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [config.checkInterval, checkSession]);

  // ----------------------------------------
  // Cross-tab synchronization
  // ----------------------------------------
  useEffect(() => {
    const handleStorage = (e) => {
      // Another tab logged out
      if (e.key === config.keys.logout) {
        window.location.href = "/";
        return;
      }

      // Another tab had activity - sync
      if (e.key === config.keys.lastActivity && e.newValue) {
        const newTime = parseInt(e.newValue, 10);
        if (newTime > lastActivityRef.current) {
          lastActivityRef.current = newTime;
          setShowWarning(false);
        }
      }
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [config.keys]);

  // ----------------------------------------
  // Cleanup on unmount
  // ----------------------------------------
  useEffect(() => {
    return () => {
      if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
      if (activityTimeoutRef.current) clearTimeout(activityTimeoutRef.current);
    };
  }, []);

  // ----------------------------------------
  // Format time remaining
  // ----------------------------------------
  const formatTimeLeft = useCallback(() => {
    if (timeLeft === null) return "--:--";
    const minutes = Math.floor(timeLeft / 60000);
    const seconds = Math.floor((timeLeft % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  }, [timeLeft]);

  // ----------------------------------------
  // Return
  // ----------------------------------------
  return {
    showWarning,
    timeLeft,
    isActive,
    formatTimeLeft,
    extendSession,
    logout,
    updateActivity,
  };
}

export default useSessionManager;